// fileUploadHandler.js
import heic2any from "heic2any";
import { uploadFile } from "./s3Helper";
import { avifToPng, postMediaModeration } from "../services/api";
import {
  routeImageThroughModeration,
  MODERATION_SOURCE,
} from "./mediaModeration";

// Phase A: video moderation is gated behind a build flag (base OFF; staging
// "true") so the video-scan trigger can be rolled out independently of images.
const VIDEO_MODERATION_ENABLED =
  process.env.REACT_APP_MODERATION_VIDEO === "true";

// Read the logged-in user id the lightweight inline way (matches utils/helpers
// getLoggedInUser) so the moderation payload can carry user_id WITHOUT importing
// the heavy helpers barrel (which pulls the SegmentBuilder/Froala tree). The
// backend derives the user from auth regardless (image-mod uses request.user, not
// this field), so this is a self-documenting redundancy + a targeting hook for
// the wheel/segment-less (unsaved) case — never the security source.
function readUserId() {
  try {
    const parsed = JSON.parse(localStorage.getItem("keyCloak") || "{}");
    return parsed && parsed.user && parsed.user.id != null
      ? parsed.user.id
      : null;
  } catch (e) {
    return null;
  }
}

// Fire the moderation scan for an uploaded asset. The backend now quarantines a
// flagged/errored asset server-side (Phase A), so this remains async: the
// enforcement no longer depends on the client. A dispatch failure is logged (the
// asset is still covered by the server-side chokepoint work in Phase B).
//
// The payload carries wheel_id/segment_id when the asset belongs to a SAVED wheel
// (so a flagged asset can be quarantined by id); on an UNSAVED wheel both are null
// because no server-side id exists yet — the backend then moderates by user + URL
// only (image-mod: `moderate_image_task.delay(image_url, user.id, None, None)`).
// user_id is included so that unsaved-context scan is still attributable to a user
// even though the wheel/segment can't be.
const triggerModeration = async ({
  image_url,
  wheel_id,
  segment_id,
  media_type,
}) => {
  try {
    await postMediaModeration({
      image_url,
      wheel_id: wheel_id != null ? wheel_id : null,
      segment_id: segment_id != null ? segment_id : null,
      media_type,
      user_id: readUserId(),
    });
  } catch (moderationError) {
    console.error("Media moderation failed:", moderationError);
  }
};

// BUG-render-parity-media-9da348 — every `object_path` this app ever presigns
// MUST fall under the caller's own `uploads/<user_id>/...` namespace: that is
// the only server-side control (PRESIGN_UPLOAD_HARDENING_ENABLED) standing
// between a presigned PUT and a cross-account S3-write IDOR (any authed user
// could otherwise sign a PUT into ANY other user's key). A `handleFileUpload`
// call with no `bucketName` (Editor.js, FroalaEditor, NewWheelPopup,
// PersonalSettings, Business Box logo, FeedbackModal screenshot — none of
// which have a wheel/business/profile folder helper to reach for) produced a
// bare top-level key (`buildUniqueObjectKey(undefined, name)` has no prefix
// at all), which is exactly the "unprefixed key" gap that keeps the BE flag
// off everywhere. Falling back to the logged-in user's own `uploads/<id>/misc/`
// namespace here — the single shared choke point (G-118) — closes the gap for
// every one of those call sites at once instead of touching each of them.
const FALLBACK_UPLOAD_FOLDER = (userId) =>
  userId != null ? `uploads/${userId}/misc/` : null;

// ISP-9999 (gordon-0923-video-upload-11s-takes-very-long): `options.onProgress`
// / `options.signal` are opt-in pass-throughs to s3Helper#uploadFile's XHR path
// (real upload %, plus cancel via AbortController) — see s3Helper.js for why
// `fetch()` alone can't report progress. No existing caller passes them, so
// every call site not yet updated (VideoUpload, Capture) stays byte-identical.
export const handleFileUpload = async (
  file,
  bucketName,
  wheel_id = null,
  segment_id = null,
  options = {}
) => {
  try {
    let fileToUpload = file;
    const resolvedBucketName =
      bucketName || FALLBACK_UPLOAD_FOLDER(readUserId());

    if (file.type == "image/avif") {
      const response = await avifToPng(file);

      if (response.data.data) {
        const pngUrl = response.data.data?.png_url;
        // Route the converted PNG through moderation too — previously AVIF
        // uploads returned here and bypassed moderation entirely (audit #24).
        if (pngUrl) {
          await triggerModeration({
            image_url: pngUrl,
            wheel_id,
            segment_id,
            media_type: "image",
          });
        }
        return { fileType: file.type, location: pngUrl };
      } else return false;
    }

    if (file.type === "image/heic") {
      const convertedBlob = await heic2any({
        blob: file,
        toType: "image/jpeg",
      });
      fileToUpload = new File(
        [convertedBlob],
        file.name.replace(/\.[^/.]+$/, ".jpg"),
        { type: "image/jpeg" }
      );
    }

    const { location, fileKey, fileType, poster } = await uploadFile(
      fileToUpload,
      resolvedBucketName,
      options
    );

    // Call media moderation after a successful upload.
    //  - IMAGES route through the SHARED moderation wrapper (the same choke point
    //    the stock/curated library selection uses; fail-open, never throws).
    //  - VIDEO is gated by the build flag (audit gap: all video was unmoderated,
    //    #2/#10 etc) and uses the media_type-aware trigger.
    if (location && fileType) {
      const isImage =
        fileType.startsWith("image/") || fileType === "image/webp";
      const isVideo = fileType.startsWith("video/");
      if (isImage) {
        await routeImageThroughModeration({
          image_url: location,
          source: MODERATION_SOURCE.UPLOAD,
          wheel_id,
          segment_id,
        });
      } else if (isVideo && VIDEO_MODERATION_ENABLED) {
        await triggerModeration({
          image_url: location,
          wheel_id,
          segment_id,
          media_type: "video",
        });
      }
    }

    // T22.168: pass through the GIF poster uploadFile just generated for THIS
    // file (null when none) — a caller replacing a gif needs the new poster,
    // not whatever poster the previous file had.
    return { fileType, location, fileKey, poster: poster || null };
  } catch (err) {
    // T22.170: a user Cancel must reach the caller as UploadCancelledError —
    // flattening it into the generic string below made UploadMedia show
    // "An error occurred while processing the file." for a Cancel.
    // (Checked by flag, not instanceof: many tests mock ./s3Helper wholesale.)
    if (err && err.cancelled === true) throw err;
    console.error(err);
    throw "An error occurred while processing the file."; // Re-throw the error so that the caller knows about the failure.
  }
};

export function base64ToFile(base64String, fileName = "wheel_image") {
  // Split the base64 string to get the data part and metadata
  const [metadata, data] = base64String.split(",");

  // Extract the file type from metadata
  const matches = metadata.match(/data:(.*);/);
  const fileType = matches ? matches[1] : "image/jpeg";

  // Get the file extension from the file type
  let extension = fileType.split("/")[1];

  // Handle special cases for file extensions
  if (extension === "svg+xml") {
    extension = "svg";
  }

  // Ensure fileName has the correct extension
  const fileNameWithExt = fileName.endsWith(`.${extension}`)
    ? fileName
    : `${fileName}.${extension}`;

  // Convert the base64 data to a binary array
  const binaryData = atob(data);

  // Create an array buffer from the binary data
  const arrayBuffer = new ArrayBuffer(binaryData.length);
  const uint8Array = new Uint8Array(arrayBuffer);
  for (let i = 0; i < binaryData.length; i++) {
    uint8Array[i] = binaryData.charCodeAt(i);
  }

  // Create a Blob from the array buffer
  const blob = new Blob([uint8Array], { type: fileType });

  // Create a File from the Blob
  const file = new File([blob], fileNameWithExt, { type: fileType });

  return file;
}

export const getWheelUploadPath = ({ user_id, wheel_id } = {}) => {
  // Never bake a literal "undefined" into an S3 key. An unsaved wheel has no id
  // yet, so a missing wheel_id previously produced ".../wheels/undefined/..." —
  // a malformed key that collides across every unsaved wheel and reads as a bug
  // in moderation/quarantine logs. Fall back to a DEFINED "unsaved" segment
  // (still namespaced under the user). Saved wheels are byte-identical to before.
  const uid = user_id != null && user_id !== "" ? user_id : "unknown";
  const wid = wheel_id != null && wheel_id !== "" ? wheel_id : "unsaved";
  return `uploads/${uid}/wheels/${wid}/`;
};

export const getBusinessBoxPath = ({ user_id }) => {
  return `uploads/${user_id}/business_box/`;
};

export const getUserProfilePath = ({ user_id }) => {
  return `uploads/${user_id}/user_profile/`;
};
